import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, Mail, Check } from 'lucide-react';
import StepIndicator from '../components/StepIndicator';
import AnimatedCounter from '../components/AnimatedCounter';
import Reveal from '../components/Reveal';
import ThemeToggle from '../components/ThemeToggle';
import '../App.css';

const API_URL = 'https://orca-transports.onrender.com/api';

const QUOTE_LOADING_MESSAGES = [
  "Verifying your details...",
  "Checking best available routes...",
  "Contacting our dispatch network...",
  "Almost there..."
];

// Fires a GA4 event via the gtag.js snippet in public/index.html.
// No-ops if gtag isn't present (e.g. blocked by an ad blocker).
const trackEvent = (name, params) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
};

// TRACK RECORD BASELINE - figures as of this date, growing automatically over time
const STATS_BASELINE_DATE = new Date('2026-07-05T00:00:00Z');
const BASE_JOBS = 2000;
const JOBS_PER_DAY = 3;
const BASE_REVIEWS = 250;
const REVIEWS_PER_WEEK = 5;

function getJobsCount() {
  const daysElapsed = Math.max(0, Math.floor((Date.now() - STATS_BASELINE_DATE.getTime()) / 86400000));
  return BASE_JOBS + daysElapsed * JOBS_PER_DAY;
}

function getReviewsCount() {
  const weeksElapsed = Math.max(0, Math.floor((Date.now() - STATS_BASELINE_DATE.getTime()) / (86400000 * 7)));
  return BASE_REVIEWS + weeksElapsed * REVIEWS_PER_WEEK;
}

const TESTIMONIALS = [
  { text: "Excellent service as usual, would highly recommend.", name: "Joanne", company: "Ultimate Express" },
  { text: "Thank you for a great job, we look forward to working with you again soon!", name: "Shauna Witts", company: "Macmillan Courier Services" },
  { text: "Great company to deal with. Delivered in perfect time, no issues at all. 5 stars!", name: "Steven Clementson", company: "Verified Client" },
  { text: "Fantastic service and communication, will definitely use again, thanks very much.", name: "Fast Turnaround Logistics Ltd", company: "" },
  { text: "Arrived on time, collected, delivered without any issues. Cannot complain — 5 star service, highly recommend.", name: "First Choice Couriers Ltd", company: "" },
  { text: "Top class! Great service, highly recommended, will definitely use again.", name: "Transgat Logistics Ltd", company: "" },
  { text: "Fantastic company, very reliable and professional, would definitely work with again. Many thanks.", name: "Verified Client", company: "" },
  { text: "Cannot fault the service — turned up quickly to fix a job that was running late, rescheduled his route to keep everything on time, and understood exactly what we needed. Very efficient, will definitely use again.", name: "Jack Anderson", company: "SCC Logistics Ltd" },
  { text: "Thank you for your excellent service with this load, we would highly recommend using this courier! Hopefully we will work together many times in the future.", name: "Victoria Adams", company: "First 4 Sameday Ltd" },
  { text: "Great service! They were on time, professional and handled our delivery with care. Communication was spot on, highly recommend!", name: "Arun Tuli", company: "Art Distribution Ltd" },
  { text: "Excellent service, reliable and trustworthy, highly recommended, definitely recommended.", name: "Sharon Paula Hodgson", company: "Transgat Logistics Ltd" },
  { text: "Great service! They were on time, professional, and handled our delivery with care. Communication was clear, and the whole process was smooth and efficient. Highly recommend!", name: "Aaron Barlow", company: "Easylink Freight Ltd" }
];

const FAQ_ITEMS = [
  { q: "How does the instant quote work?", a: "Enter your collection and delivery postcodes, load type, and dimensions. We calculate the live driving distance and give you an estimated price range in seconds." },
  { q: "Is the price I see final?", a: "No — it's an estimate based on distance and load type. We confirm the final price with you directly before any booking is made." },
  { q: "Why do I need to verify my email?", a: "We send a one-time code to your email before processing a quote or enquiry. This confirms we can reach you and helps keep spam requests out of our system." },
  { q: "I didn't receive my verification code — what do I do?", a: "Check your spam/promotions folder first. If it's not there, use the \"Resend Code\" link that appears after 30 seconds on the verification screen." },
  { q: "What areas do you cover?", a: "We cover nationwide UK routes, from small vans to XLWB Sprinters." },
  { q: "What types of loads can you carry?", a: "Standard dry goods, frozen/chilled loads, pharma, pallets, loose cartons, and irregular freight — up to 4.3m length and 2m height." },
  { q: "How do I actually book after getting a quote?", a: "Once you have your estimate, use the \"Contact Us Now\" call button to speak with us directly and confirm your booking." },
  { q: "Do you offer business or corporate accounts?", a: "Yes — use the Business Accounts form to request a callback about priority booking and volume rates." }
];

function Home() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [step, setStep] = useState(1);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [error, setError] = useState('');

  // STATE: Includes new Length, Height, Date fields
  const [form, setForm] = useState({
    collection: '', delivery: '', loadType: 'dry',
    length: '', height: '',
    name: '', email: '', phone: '',
    collectionDate: '', collectionTime: '',
    deliveryDate: '', deliveryTime: ''
  });
  const [otp, setOtp] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);

  const [contractStep, setContractStep] = useState(1);
  const [contractOtp, setContractOtp] = useState('');
  const [contractForm, setContractForm] = useState({ company: '', phone: '', email: '', message: '' });
  const [contractOtpCooldown, setContractOtpCooldown] = useState(0);
  const [quoteLoadingMsg, setQuoteLoadingMsg] = useState(0);

  useEffect(() => {
    if (otpCooldown === 0) return;
    const t = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  useEffect(() => {
    if (!quoteLoading) { setQuoteLoadingMsg(0); return; }
    const t = setInterval(() => setQuoteLoadingMsg(i => (i + 1) % QUOTE_LOADING_MESSAGES.length), 3500);
    return () => clearInterval(t);
  }, [quoteLoading]);

  useEffect(() => {
    if (contractOtpCooldown === 0) return;
    const t = setTimeout(() => setContractOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [contractOtpCooldown]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleContractChange = (e) => setContractForm({ ...contractForm, [e.target.name]: e.target.value });

  // VALIDATION
  const isValidUKPostcode = (postcode) => {
    const regex = /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/;
    return regex.test(postcode.trim());
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // QUOTE LOGIC
  const handleGetQuote = async () => {
    setError('');
    if (!form.name || !form.email || !form.phone || !form.collection || !form.delivery || !form.collectionDate) {
      setError("❌ Please fill in all fields."); return;
    }
    if (!isValidEmail(form.email)) { setError("❌ Invalid Email Address"); return; }
    if (!isValidUKPostcode(form.collection)) { setError("❌ Invalid Collection Postcode"); return; }
    if (!isValidUKPostcode(form.delivery)) { setError("❌ Invalid Delivery Postcode"); return; }

    setQuoteLoading(true);
    try {
      await axios.post(`${API_URL}/send-otp`, { email: form.email });
      trackEvent('quote_form_submit', { load_type: form.loadType });
      setStep(2);
      setOtpCooldown(30);
    } catch (err) { setError("Server error."); }
    setQuoteLoading(false);
  };

  const handleResendQuoteOtp = async () => {
    if (otpCooldown > 0) return;
    setError('');
    try {
      await axios.post(`${API_URL}/send-otp`, { email: form.email });
      setOtpCooldown(30);
    } catch (err) { setError("Failed to resend code."); }
  };

  const handleVerifyQuote = async () => {
    setQuoteLoading(true);
    try {
      const verifyRes = await axios.post(`${API_URL}/verify-otp`, { email: form.email, code: otp });
      if (verifyRes.data.success) {
        const distRes = await axios.post(`${API_URL}/get-distance`, { origin: form.collection, destination: form.delivery });
        if (!distRes.data.success) {
          setError("❌ Unable to calculate distance for these postcodes right now. Please call us directly.");
          setQuoteLoading(false);
          return;
        }
        const miles = parseFloat(distRes.data.miles);

        const isFridge = (form.loadType === 'frozen' || form.loadType === 'pharma');
        let minRate = isFridge ? 1.85 : 1.2;
        let maxRate = isFridge ? 2.20 : 1.5;
        if (miles > 100) { minRate -= 0.2; maxRate -= 0.2; }

        let low = (miles * minRate).toFixed(0);
        let high = (miles * maxRate).toFixed(0);
        if (low < 45) low = 45; if (high < 45) high = 45;

        // SEND QUOTE TO BACKEND
        await axios.post(`${API_URL}/send-quote`, {
          ...form,
          collectionDate: `${form.collectionDate} ${form.collectionTime}`,
          deliveryDate: `${form.deliveryDate} ${form.deliveryTime}`,
          distance: miles,
          price: { low, high }
        });

        trackEvent('generate_lead', { lead_type: 'instant_quote', value: Number(high), currency: 'GBP' });
        navigate('/thank-you', { state: { type: 'quote', email: form.email, low, high } });
      } else { setError("❌ Invalid Code"); }
    } catch (err) { setError("Verification Failed"); }
    setQuoteLoading(false);
  };

  // CONTRACT LOGIC
  const handleContractSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(contractForm.email)) { alert("❌ Invalid Email Address"); return; }
    setContractLoading(true);
    try {
      await axios.post(`${API_URL}/send-otp`, { email: contractForm.email });
      trackEvent('business_enquiry_submit', {});
      setContractStep(2);
      setContractOtpCooldown(30);
    } catch (err) { alert("Failed to send code."); }
    setContractLoading(false);
  };

  const handleResendContractOtp = async () => {
    if (contractOtpCooldown > 0) return;
    try {
      await axios.post(`${API_URL}/send-otp`, { email: contractForm.email });
      setContractOtpCooldown(30);
    } catch (err) { alert("Failed to resend code."); }
  };

  const handleVerifyContract = async () => {
    setContractLoading(true);
    try {
      const verifyRes = await axios.post(`${API_URL}/verify-otp`, { email: contractForm.email, code: contractOtp });
      if (verifyRes.data.success) {
        await axios.post(`${API_URL}/send-business-inquiry`, {
            businessName: contractForm.company,
            email: contractForm.email,
            contactNumber: contractForm.phone,
            description: contractForm.message
        });
        trackEvent('generate_lead', { lead_type: 'business_account' });
        navigate('/thank-you', { state: { type: 'business', email: contractForm.email, company: contractForm.company } });
      } else { alert("Invalid Code"); }
    } catch (err) { alert("Verification Failed"); }
    setContractLoading(false);
  };

  return (
    <div className="page-wrapper">

      {/* 1. NAVBAR */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="logo"><img src="/orca.png" alt="" className="nav-logo-icon" />ORCA TRANSPORTS</div>
        <div className="nav-right">
          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
            <a href="#services" onClick={() => setMenuOpen(false)}>Services</a>
            <a href="#quote" onClick={() => setMenuOpen(false)}>Quote</a>
            <a href="#contact" onClick={() => setMenuOpen(false)}>Business</a>
            <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
          </div>
          <ThemeToggle />
          <button className="hamburger-btn" aria-label="Toggle menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      {/* 2. BANNER */}
      <header className="hero" id="about">
        <h1>ORCA TRANSPORTS LTD</h1>
        <p>Reliable logistics from Small Vans to XLWB Sprinters. Nationwide coverage.</p>
        <a href="#quote" className="cta-btn">GET QUOTE</a>

        <div className="hero-badge">
          <div className="hero-badge-item">
            <strong>Instant</strong>
            <span>Online Quotes</span>
          </div>
          <div className="hero-badge-divider"></div>
          <div className="hero-badge-item">
            <strong>Free</strong>
            <span>No Obligation</span>
          </div>
          <div className="hero-badge-divider"></div>
          <div className="hero-badge-item">
            <strong>Fast</strong>
            <span>Reply Within Minutes</span>
          </div>
        </div>
      </header>

      {/* 3. SIX CARDS (SERVICES) - MOVED UP HERE */}
      <section className="section" id="services">
        <h2 className="section-title">Comprehensive Fleet Services</h2>
        <div className="section-title-accent"></div>
        <div className="services-grid">
          <Reveal><div className="service-card"><div className="icon-chip"><span className="icon">🚛</span></div><h3>Versatile Fleet</h3><p>From Small Vans to XLWB Sprinters. Loads up to 4.3m length & 2m height.</p></div></Reveal>
          <Reveal delay={80}><div className="service-card"><div className="icon-chip"><span className="icon">🏢</span></div><h3>B2B & Multi-Drop</h3><p>Complex route planning for business deliveries.</p></div></Reveal>
          <Reveal delay={160}><div className="service-card"><div className="icon-chip"><span className="icon">📦</span></div><h3>Pallets & Parcels</h3><p>Handling standard pallets, loose cartons, and irregular freight safely.</p></div></Reveal>
          <Reveal><div className="service-card"><div className="icon-chip"><span className="icon">❄️</span></div><h3>Frozen & Pharma</h3><p>Temperature-controlled vehicles for frozen (-18°C) and pharma.</p></div></Reveal>
          <Reveal delay={80}><div className="service-card"><div className="icon-chip"><span className="icon">⚡</span></div><h3>Same Day Express</h3><p>Dedicated vehicles for same-day delivery across the UK.</p></div></Reveal>
          <Reveal delay={160}><div className="service-card"><div className="icon-chip"><span className="icon">🎪</span></div><h3>Events & Exhibition</h3><p>Reliable logistics for trade shows.</p></div></Reveal>
        </div>
      </section>

      {/* 3B. TRACK RECORD */}
      <section className="stats-band">
        <Reveal>
          <div className="stats-grid">
            <div className="stat-item">
              <AnimatedCounter target={getJobsCount()} suffix="+" />
              <p>Jobs Completed<br/><span>Last 18 Months</span></p>
            </div>
            <div className="stat-item">
              <AnimatedCounter target={getReviewsCount()} suffix="+" />
              <p>Customer Reviews<br/><span>98%+ Positive</span></p>
            </div>
            <div className="stat-item">
              <h3>24/7</h3>
              <p>Open & Delivering<br/><span>Round-the-Clock Operations</span></p>
            </div>
            <div className="stat-item">
              <h3>UK-Wide</h3>
              <p>Coverage<br/><span>Hubs in Wembley, Luton, Sheffield, Manchester & Glasgow</span></p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 3C. TESTIMONIALS */}
      <section className="section" id="reviews">
        <h2 className="section-title">Proof We Actually Deliver (Pun Intended)</h2>
        <div className="section-title-accent"></div>
        <p className="testimonials-subtitle">Real feedback from real hauliers and clients — no cherry-picking, just consistently on time.</p>
      </section>
      <div className="testimonials-marquee">
        <div className="testimonials-track">
          {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
            <div className="testimonial-card" key={`${t.name}-${i}`} aria-hidden={i >= TESTIMONIALS.length}>
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">"{t.text}"</p>
              <div className="testimonial-author">
                <strong>{t.name}</strong>
                {t.company && <span>{t.company}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4. QUOTE GENERATOR - MOVED DOWN HERE */}
      <section className="section" id="quote">
        <Reveal>
        <div className="quote-wrapper">
          <div className="quote-info">
            <h2>Instant Quote</h2>
            <p>Get a price in seconds.</p>
            <ul className="feature-list"><li><Check size={16} strokeWidth={2.5} /> Live Mileage Calculation</li><li><Check size={16} strokeWidth={2.5} /> Vehicle Compatibility Check</li><li><Check size={16} strokeWidth={2.5} /> Instant Email Confirmation</li></ul>

            <div className="how-it-works">
              <h4>How It Works</h4>
              <div className="hiw-step">
                <span className="hiw-num">1</span>
                <div><strong>Enter Details</strong><p>Postcodes, load type & dates</p></div>
              </div>
              <div className="hiw-step">
                <span className="hiw-num">2</span>
                <div><strong>Verify Email</strong><p>Quick one-time code</p></div>
              </div>
              <div className="hiw-step">
                <span className="hiw-num">3</span>
                <div><strong>Get Your Price</strong><p>Instant estimate, no obligation</p></div>
              </div>
            </div>

            <div className="quote-help">
              <p>Prefer to talk?</p>
              <a href="tel:+447501046288"><Phone size={15} strokeWidth={2} /> +44 7501 046288</a>
              <a href="https://wa.me/447501046288?text=Hi%2C%20I%27d%20like%20a%20quote%20from%20Orca%20Transports" target="_blank" rel="noopener noreferrer"><MessageCircle size={15} strokeWidth={2} /> Chat on WhatsApp</a>
            </div>
          </div>
          <div className="quote-form-container">
            <StepIndicator current={step} labels={['Details', 'Verify', 'Quote']} />
            {error && <div className="error-msg">{error}</div>}
            {step === 1 && (
              <div className="form-stack">
                <div className="form-row"><div className="field full"><label htmlFor="q-name">Full Name / Company</label><input id="q-name" className="neat-input" name="name" placeholder="Enter Name" onChange={handleChange} /></div></div>
                <div className="form-row"><div className="field half"><label htmlFor="q-email">Email Address</label><input id="q-email" className="neat-input" type="email" name="email" placeholder="email@example.com" onChange={handleChange} /></div><div className="field half"><label htmlFor="q-phone">Mobile</label><input id="q-phone" className="neat-input" type="tel" name="phone" placeholder="Number" onChange={handleChange} /></div></div>

                {/* DATES */}
                <div className="form-row">
                    <div className="field half"><label htmlFor="q-collection-date">Collection Date</label><input id="q-collection-date" className="neat-input" type="date" name="collectionDate" onChange={handleChange} /></div>
                    <div className="field half"><label htmlFor="q-delivery-date">Delivery Date</label><input id="q-delivery-date" className="neat-input" type="date" name="deliveryDate" onChange={handleChange} /></div>
                </div>

                {/* DIMS */}
                <div className="form-row">
                    <div className="field half"><label htmlFor="q-length">Max Length (m)</label><input id="q-length" className="neat-input" name="length" placeholder="Max 4.3m" onChange={handleChange} /></div>
                    <div className="field half"><label htmlFor="q-height">Max Height (m)</label><input id="q-height" className="neat-input" name="height" placeholder="Max 2m" onChange={handleChange} /></div>
                </div>

                <div className="form-row"><div className="field half"><label htmlFor="q-collection">Collection Postcode</label><input id="q-collection" className="neat-input" name="collection" placeholder="e.g. LU1 3BS" onChange={handleChange} /></div><div className="field half"><label htmlFor="q-delivery">Delivery Postcode</label><input id="q-delivery" className="neat-input" name="delivery" placeholder="e.g. M1 1AA" onChange={handleChange} /></div></div>
                <div className="form-row"><div className="field full"><label htmlFor="q-load-type">Load Type</label><select id="q-load-type" className="neat-input" name="loadType" onChange={handleChange}><option value="dry">Standard Dry Goods</option><option value="frozen">Frozen / Chilled</option><option value="pharma">Pharma</option></select></div></div>
                <button className="full-btn" onClick={handleGetQuote} disabled={quoteLoading}>{quoteLoading ? "Processing..." : "CHECK PRICE"}</button>
                {quoteLoading && <p className="loading-status">{QUOTE_LOADING_MESSAGES[quoteLoadingMsg]}</p>}
              </div>
            )}
            {step === 2 && (
              <div className="center-content">
                <h3>Verify Email</h3>
                <p>Enter code sent to {form.email}</p>
                <label htmlFor="quote-otp" className="sr-only">Verification code</label>
                <input id="quote-otp" className="otp-input" maxLength="4" inputMode="numeric" onChange={(e) => setOtp(e.target.value)} />
                <button onClick={handleVerifyQuote} disabled={quoteLoading}>{quoteLoading ? "Verifying..." : "VIEW QUOTE"}</button>
                <p className="resend-row">
                  {otpCooldown > 0
                    ? <span className="resend-muted">Didn't get it? Resend in {otpCooldown}s</span>
                    : <button type="button" className="link-btn" onClick={handleResendQuoteOtp}>Resend Code</button>}
                </p>
              </div>
            )}
          </div>
        </div>
        </Reveal>
      </section>

      {/* 5. BUSINESS FORM */}
      <section className="section" id="contact">
        <Reveal>
        <div className="contract-section">
          <h2>Business Accounts</h2>
          <div className="section-title-accent"></div>
          <p className="subtitle">Join our network for priority booking and volume rates.</p>
          <StepIndicator current={contractStep} labels={['Details', 'Verify', 'Done']} />
          {contractStep === 1 && (
            <form onSubmit={handleContractSubmit} className="contract-form-neat">
              <div className="form-row">
                <label htmlFor="c-company" className="sr-only">Company Name</label>
                <input id="c-company" name="company" placeholder="Company Name" required onChange={handleContractChange} className="neat-input" />
              </div>
              <div className="form-row split">
                <label htmlFor="c-email" className="sr-only">Business Email</label>
                <input id="c-email" type="email" name="email" placeholder="Business Email" required onChange={handleContractChange} className="neat-input" />
                <label htmlFor="c-phone" className="sr-only">Contact Number</label>
                <input id="c-phone" type="tel" name="phone" placeholder="Contact Number" required onChange={handleContractChange} className="neat-input" />
              </div>
              <div className="form-row">
                <label htmlFor="c-message" className="sr-only">How can we help?</label>
                <textarea id="c-message" name="message" placeholder="How can we help?" rows="4" onChange={handleContractChange} className="neat-input"></textarea>
              </div>
              <button type="submit" className="full-btn neat-btn" disabled={contractLoading}>REQUEST CALLBACK</button>
            </form>
          )}
          {contractStep === 2 && (
            <div className="center-content">
              <h3>Verify Email</h3>
              <label htmlFor="contract-otp" className="sr-only">Verification code</label>
              <input id="contract-otp" className="otp-input" maxLength="4" inputMode="numeric" onChange={(e) => setContractOtp(e.target.value)} />
              <button onClick={handleVerifyContract} disabled={contractLoading}>{contractLoading ? "Verifying..." : "CONFIRM"}</button>
              <p className="resend-row">
                {contractOtpCooldown > 0
                  ? <span className="resend-muted">Didn't get it? Resend in {contractOtpCooldown}s</span>
                  : <button type="button" className="link-btn" onClick={handleResendContractOtp}>Resend Code</button>}
              </p>
            </div>
          )}
        </div>
        </Reveal>
      </section>

      {/* 6. FAQ */}
      <section className="section" id="faq">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="section-title-accent"></div>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <Reveal key={item.q} delay={Math.min(i, 4) * 60}>
            <div className={`faq-item ${openFaq === i ? 'open' : ''}`}>
              <button type="button" className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                {item.q}
                <span className="faq-icon">{openFaq === i ? '−' : '+'}</span>
              </button>
              {openFaq === i && <p className="faq-answer">{item.a}</p>}
            </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 7. FOOTER */}
      <footer>
        <div className="footer-grid">
          <div className="footer-col">
            <div className="footer-brand">
              <img src="/orca.png" alt="" className="footer-logo" />
              <h3>ORCA TRANSPORTS LTD</h3>
            </div>
            <p>Reliable logistics from Small Vans to XLWB Sprinters. Nationwide coverage.</p>
          </div>
          <div className="footer-col">
            <h4>Quick Links</h4>
            <nav className="footer-quicklinks">
              <a href="#about">About</a>
              <a href="#services">Services</a>
              <a href="#quote">Quote</a>
              <a href="#contact">Business</a>
              <a href="#faq">FAQ</a>
            </nav>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <p className="footer-address"><MapPin size={15} strokeWidth={2} /> Victory House, Chobham Street, Luton, LU3 3JS</p>
            <a href="tel:+447501046288"><Phone size={15} strokeWidth={2} /> +44 7501 046288</a>
            <a href="mailto:sales@orcatransports.com"><Mail size={15} strokeWidth={2} /> sales@orcatransports.com</a>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-links">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <span className="footer-dot">•</span>
            <Link to="/terms-of-service">Terms of Service</Link>
          </div>
          <p>© 2026 Orca Transports Ltd.</p>
        </div>
      </footer>

      {/* FLOATING WHATSAPP BUTTON */}
      <a
        href="https://wa.me/447501046288?text=Hi%2C%20I%27d%20like%20a%20quote%20from%20Orca%20Transports"
        className="whatsapp-fab"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 32 32" width="30" height="30" fill="white" aria-hidden="true">
          <path d="M16 4C9.4 4 4 9.4 4 16c0 2.2.6 4.3 1.7 6.1L4 28l6.1-1.6C11.8 27.4 13.9 28 16 28c6.6 0 12-5.4 12-12S22.6 4 16 4zm6.9 16.9c-.3.8-1.6 1.5-2.3 1.6-.6.1-1.3.1-2.1-.1-.5-.1-1.1-.3-1.9-.7-3.4-1.5-5.6-4.9-5.8-5.1-.2-.2-1.4-1.9-1.4-3.6 0-1.7.9-2.6 1.2-2.9.3-.3.7-.4 1-.4h.7c.2 0 .5 0 .7.6.3.7.9 2.4 1 2.6.1.2.2.4.1.6-.1.2-.2.4-.4.6-.2.2-.4.5-.6.6-.2.2-.4.4-.2.8.2.4 1 1.6 2.1 2.6 1.4 1.3 2.6 1.7 3 1.9.4.2.6.1.8-.1.2-.2.9-1 1.1-1.4.2-.4.5-.3.8-.2.3.1 2 1 2.4 1.1.4.2.6.3.7.4.1.3.1.9-.2 1.7z"/>
        </svg>
        <span className="whatsapp-tooltip">Chat on WhatsApp</span>
      </a>
    </div>
  );
}

export default Home;
